import { Inbox, Keyboard, Loader2, MailOpen, Mail as MailIcon, Menu, Paperclip, Search, ShieldAlert, Star, Trash2, Undo2, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Kbd } from '@/components/ui/kbd'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { MailBoxApi } from '../hooks/useMailBox'
import { groupByDate } from '../mailDates'
import { MAIL_SEARCH_ID, useViewLabel } from '../mailView'
import { MailListRow } from './MailListRow'

const CHIPS = ['all', 'unread', 'starred', 'attachments'] as const
type Chip = (typeof CHIPS)[number]
const CHIP_ICON = { all: Inbox, unread: MailIcon, starred: Star, attachments: Paperclip } as const

type Props = { box: MailBoxApi; onOpenNav: () => void; onShortcuts: () => void }

/** Konuşma listesi: arama, hızlı filtreler, toplu seçim ve tarih başlıkları. */
export function MailList({ box, onOpenNav, onShortcuts }: Props) {
  const { t } = useTranslation()
  const label = useViewLabel(box)
  const [chip, setChip] = useState<Exclude<Chip, 'unread'>>('all')
  const [picked, setPicked] = useState<Set<string>>(() => new Set())
  const [now] = useState(() => new Date())
  const active: Chip = box.unreadOnly ? 'unread' : chip

  const rows = box.filtered.filter((m) => (active === 'starred' ? m.is_favorite : active === 'attachments' ? (m.attachments?.length ?? 0) > 0 : true))
  const ids = rows.map((m) => m.id)
  const selected = ids.filter((id) => picked.has(id))
  const unread = box.filtered.filter((m) => !m.is_read).length
  const inTrash = box.view.kind === 'folder' && box.view.folder === 'TRASH'
  const inSpam = box.view.kind === 'folder' && box.view.folder === 'SPAM'

  const pickChip = (c: Chip) => {
    box.setUnreadOnly(c === 'unread')
    if (c !== 'unread') setChip(c)
  }
  const toggle = (id: string) =>
    setPicked((p) => {
      const n = new Set(p)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  const clear = () => setPicked(new Set())
  /** Onaylı işlemler (çöp, spam, kalıcı sil) vazgeçilirse false döner; o durumda seçim korunur. */
  const bulk = (fn: () => void | boolean | Promise<boolean>) => {
    void Promise.resolve(fn()).then((done) => {
      if (done !== false) clear()
    })
  }
  const allChecked = selected.length > 0 && selected.length === ids.length

  return (
    <section className="flex h-full min-w-0 flex-col bg-card" aria-label={label(box.view)}>
      <header className="space-y-3 border-b px-3 pb-3 pt-3 sm:px-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="size-9 shrink-0 xl:hidden" onClick={onOpenNav} aria-label={t('mail.mailbox.nav')}>
            <Menu />
          </Button>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold tracking-tight">{label(box.view)}</h2>
            <p className="text-xs text-muted-foreground">
              {t('mail.mailbox.listMeta', { count: box.filtered.length })}
              {unread ? ` · ${t('mail.mailbox.unreadMeta', { count: unread })}` : ''}
            </p>
          </div>
          <Tooltip content={t('mail.mailbox.shortcuts.title')}>
            <Button variant="ghost" size="icon" className="hidden size-9 md:inline-flex" onClick={onShortcuts} aria-label={t('mail.mailbox.shortcuts.title')}>
              <Keyboard />
            </Button>
          </Tooltip>
        </div>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            id={MAIL_SEARCH_ID}
            value={box.search}
            onChange={(e) => box.setSearch(e.target.value)}
            placeholder={t('mail.mailbox.search')}
            aria-label={t('mail.mailbox.search')}
            className="h-10 rounded-xl border-transparent bg-muted/60 pl-9 pr-16 focus-visible:bg-card"
          />
          {box.search ? (
            <button type="button" onClick={() => box.setSearch('')} className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted" aria-label={t('common.clear')}>
              <X className="size-3.5" />
            </button>
          ) : (
            <Kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 md:inline-flex">/</Kbd>
          )}
        </div>

        {selected.length ? (
          <div className="flex min-h-8 items-center gap-1 rounded-xl bg-primary/[0.07] px-2 py-1">
            <input type="checkbox" checked={allChecked} onChange={() => (allChecked ? clear() : setPicked(new Set(ids)))} className="mx-1 size-4 accent-[var(--color-primary)]" aria-label={t('mail.mailbox.bulk.selectAll')} />
            <span className="mr-auto text-xs font-semibold text-primary">{t('mail.mailbox.bulk.selected', { count: selected.length })}</span>
            <BulkButton icon={MailOpen} label={t('mail.mailbox.bulk.read')} onClick={() => bulk(() => box.setRead(selected, true))} />
            <BulkButton icon={MailIcon} label={t('mail.mailbox.bulk.unread')} onClick={() => bulk(() => box.setRead(selected, false))} />
            <BulkButton icon={Star} label={t('mail.mailbox.bulk.star')} onClick={() => bulk(() => box.setFavoriteMany(selected, true))} />
            {inTrash || inSpam ? (
              <>
                <BulkButton icon={Undo2} label={t('mail.mailbox.bulk.restore')} onClick={() => bulk(() => box.moveMany(selected, 'INBOX'))} />
                <BulkButton icon={Trash2} label={t('mail.mailbox.actions.deleteForever')} onClick={() => bulk(() => box.deletePermanentMany(selected))} />
              </>
            ) : (
              <>
                <BulkButton icon={ShieldAlert} label={t('mail.mailbox.actions.spam')} onClick={() => bulk(() => box.moveMany(selected, 'SPAM'))} />
                <BulkButton icon={Trash2} label={t('mail.mailbox.actions.trash')} onClick={() => bulk(() => box.moveMany(selected, 'TRASH'))} />
              </>
            )}
            <BulkButton icon={X} label={t('mail.mailbox.bulk.clear')} onClick={clear} />
          </div>
        ) : (
          <div className="flex gap-1.5 overflow-x-auto scrollbar-none">
            {CHIPS.map((c) => {
              const Icon = CHIP_ICON[c]
              const on = active === c
              return (
                <button
                  key={c}
                  type="button"
                  aria-pressed={on}
                  onClick={() => pickChip(c)}
                  className={cn(
                    'inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    on ? 'border-primary/30 bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <Icon className="size-3.5" aria-hidden />
                  {t(`mail.mailbox.chips.${c}`)}
                </button>
              )
            })}
          </div>
        )}
      </header>

      <div className="flex-1 overflow-y-auto scrollbar-none pb-20 md:pb-2">
        {rows.length === 0 && box.loading ? (
          <div className="flex justify-center py-10 text-muted-foreground">
            <Loader2 className="size-5 animate-spin" aria-label={t('common.loading')} />
          </div>
        ) : rows.length === 0 && !box.hasMore ? (
          <EmptyState icon={Inbox} title={t('mail.mailbox.empty')} description={box.search ? t('mail.mailbox.emptySearch') : t('mail.mailbox.emptyHint')} />
        ) : (
          groupByDate(rows, now).map((g) => (
            <div key={`${g.bucket}-${g.items[0]?.id}`}>
              <p className="sticky top-0 z-10 bg-card/95 px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground backdrop-blur">{t(`mail.mailbox.dates.${g.bucket}`)}</p>
              <ul className="space-y-0.5 px-2">
                {g.items.map((m) => (
                  <MailListRow key={m.id} box={box} message={m} checked={picked.has(m.id)} selecting={selected.length > 0} onCheck={() => toggle(m.id)} />
                ))}
              </ul>
            </div>
          ))
        )}
        {box.hasMore ? (
          <div className="flex justify-center px-4 py-3">
            <Button variant="outline" size="sm" onClick={box.loadMore} disabled={box.loadingMore}>
              {box.loadingMore ? <Loader2 className="animate-spin" /> : null}
              {t('mail.mailbox.loadMore')}
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  )
}

function BulkButton({ icon: Icon, label, onClick }: { icon: typeof Star; label: string; onClick: () => void }) {
  return (
    <Tooltip content={label}>
      <Button variant="ghost" size="icon" className="size-8 text-foreground/80" onClick={onClick} aria-label={label}>
        <Icon />
      </Button>
    </Tooltip>
  )
}
